import React, { useEffect, useState, useCallback } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { AlterarNickModal, PainelOficialModal, PainelPracaModal, AtribuirPerfisModal } from "../../../components/modals";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../../components/ui/dialog";
import { InputText } from "../../../components/InputText";
import { Label } from "../../../components/ui/label";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { LockOpen, Edit, Search, User, Calendar, Shield, TrendingUp, AlertTriangle, Award, Settings, UserPlus, KeyRound, X, UserCheck, MoreVertical, Pencil, Copy, Check, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { api } from "../../../services/api";
import { useAuth } from "../../../contexts/AuthContext";
import { usePermissions, PERMISSOES } from "../../../hooks/usePermissions";

// Interfaces TypeScript
interface AcessoInfo {
  status: string;
  criado_em: string;
  alterado_em: string;
}

interface Militar {
  id: number;
  nick: string;
  patente: string;
  nivel_patente: number;
  ultima_promocao: string;
  promovido_por: string;
  tem_acesso: boolean;
  acesso_ativo: boolean;
  acesso_info: AcessoInfo | null;
  status: string | null;
}

export function Militares() {
  const { militar: militarLogado } = useAuth();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const navigate = useNavigate();
  const maxOrdem = militarLogado?.max_ordem_permitida ?? 0;
  const [militares, setMilitares] = useState<Militar[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isAlterarNickModalOpen, setIsAlterarNickModalOpen] = useState(false);
  const [selectedMilitar, setSelectedMilitar] = useState<Militar | null>(null);
  const [isPainelOficialModalOpen, setIsPainelOficialModalOpen] = useState(false);
  const [isPainelPracaModalOpen, setIsPainelPracaModalOpen] = useState(false);
  const [isAtribuirPerfisModalOpen, setIsAtribuirPerfisModalOpen] = useState(false);
  const [resetPasswordResult, setResetPasswordResult] = useState<{ nick: string; senha: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [syncingWiredId, setSyncingWiredId] = useState<number | null>(null);

  // Verificar permissões por militar
  const canGerenciarPerfis = (militar: Militar) =>
    hasPermission(PERMISSOES.ATRIBUIR_PERFIS) && maxOrdem >= militar.nivel_patente;

  const canResetSenha = (militar: Militar) => {
    const permissao = militar.nivel_patente >= 12
      ? PERMISSOES.CRIAR_ALTERAR_PAINEL_OFICIAL
      : PERMISSOES.CRIAR_ALTERAR_PAINEL_PRACA;
    return hasPermission(permissao) && maxOrdem >= militar.nivel_patente;
  };

  const canAlterarNick = (militar: Militar) =>
    hasPermission(PERMISSOES.ALTERAR_NICK) && maxOrdem >= militar.nivel_patente;

  const canEditarMilitar = (militar: Militar) =>
    hasPermission(PERMISSOES.EDITAR_MILITAR) && maxOrdem >= militar.nivel_patente;

  const hasAnyAction = (militar: Militar) =>
    canGerenciarPerfis(militar) || canResetSenha(militar) || canAlterarNick(militar) || canEditarMilitar(militar);

  const PAGE_SIZE = 50;

  // Buscar militares com paginação
  const fetchMilitares = useCallback(async (nick: string, currentPage: number) => {
    setLoading(true);
    try {
      const url = nick ? `/militares/list?nick=${encodeURIComponent(nick)}` : `/militares/list`;
      const response = await api.get(url, {
        headers: { page: String(currentPage), size: String(PAGE_SIZE) },
      });
      const { militares: newMilitares, totalCount: count, totalPages: pages } = response.data;
      setMilitares(newMilitares);
      setTotalCount(count);
      setTotalPages(pages);
    } catch (error) {
      enqueueSnackbar("Erro ao buscar militares", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset de senha
  const handleResetPassword = async (militar: Militar) => {
    try {
      const data = { id_militar: militar.id };
      const endpoint = militar.nivel_patente >= 12
        ? "/militaresAcessos/resetOficial"
        : "/militaresAcessos/resetPraca";

      const response = await api.patch(endpoint, data);
      setCopied(false);
      setResetPasswordResult({ nick: militar.nick, senha: response.data.senha });
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao resetar senha", {
        variant: "error"
      });
    }
  };

  // Sincroniza patente e sigla do militar com o jogo (via fila no servidor)
  const handleSincronizarWired = async (militar: Militar) => {
    setSyncingWiredId(militar.id);
    try {
      const response = await api.post<{ enfileiradas: string[]; avisos: string[] }>(
        `/militares/${militar.id}/sincronizar-wired`
      );
      const { enfileiradas, avisos } = response.data;

      if (enfileiradas.length > 0) {
        const nomes: Record<string, string> = {
          atualizar_patente: "patente",
          atualizar_sigla: "sigla",
          atualizar_status: "status",
        };
        enqueueSnackbar(
          `Sincronização de ${militar.nick} enviada para a fila (${enfileiradas.map((c) => nomes[c] ?? c).join(", ")}).`,
          { variant: "success" }
        );
      }
      avisos.forEach((aviso) => enqueueSnackbar(aviso, { variant: "warning" }));
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao sincronizar com o WIRED", {
        variant: "error",
      });
    } finally {
      setSyncingWiredId(null);
    }
  };

  const handleCopySenha = () => {
    if (!resetPasswordResult) return;
    navigator.clipboard.writeText(resetPasswordResult.senha);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    fetchMilitares("", 1);
  }, [fetchMilitares]);

  // Funções de navegação de página
  const handlePrevPage = () => {
    if (page > 1) {
      const newPage = page - 1;
      setPage(newPage);
      fetchMilitares(searchTerm, newPage);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      const newPage = page + 1;
      setPage(newPage);
      fetchMilitares(searchTerm, newPage);
    }
  };

  // Handler para busca (reinicia a paginação)
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchMilitares(searchTerm, 1);
  };

  // Formatar data
  const formatDate = (dateString: string) => {
    return moment(dateString).format("DD/MM/YYYY");
  };

  // Abrir modal de alterar nick
  const handleAlterarNick = (militar: Militar) => {
    setSelectedMilitar(militar);
    setIsAlterarNickModalOpen(true);
  };

  // Fechar modal e recarregar dados
  const handleCloseModal = () => {
    setIsAlterarNickModalOpen(false);
    setSelectedMilitar(null);
  };

  const handleNickUpdateSuccess = () => {
    setPage(1);
    fetchMilitares(searchTerm, 1);
  };

  // Abrir modal de atribuir perfis
  const handleAtribuirPerfis = (militar: Militar) => {
    setSelectedMilitar(militar);
    setIsAtribuirPerfisModalOpen(true);
  };

  const handleCloseAtribuirPerfis = () => {
    setIsAtribuirPerfisModalOpen(false);
    setSelectedMilitar(null);
  };

  const handlePerfisUpdateSuccess = () => {
    setPage(1);
    fetchMilitares(searchTerm, 1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-border pb-4">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <User className="h-6 w-6 text-primary" />
          Militares
        </h1>
        <p className="text-muted-foreground mt-2">
          Gerencie os militares do sistema
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Conteúdo Principal */}
        <div className="lg:col-span-2 space-y-6">

            {/* Filtros de Busca */}
            <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)]">
                  <Search className="w-5 h-5" />
                  Buscar Militares
                </CardTitle>
                <CardDescription>
                  Digite o nick do militar para pesquisar ou deixe em branco para ver todos
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSearch} className="flex gap-4">
                  <div className="flex-1">
                    <Label htmlFor="search">Nick do Militar</Label>
                    <InputText
                      id="search"
                      name="search"
                      title=""
                      placeholder="Digite o nick do militar..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                    >
                      {loading ? "Buscando..." : "Buscar"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Resultados */}
            <Card>
              <CardHeader>
                <CardTitle className="text-[oklch(0.35_0.15_142)] dark:text-[oklch(0.55_0.15_142)]">Resultados</CardTitle>
                <CardDescription>
                  {militares.length} militar(es) encontrado(s)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-gray-500 dark:text-gray-400">Carregando...</div>
                  </div>
                ) : militares.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Nenhum militar encontrado
                  </div>
                ) : (
                  <div className="space-y-4">
                    {militares.map((militar) => (
                      <div
                        key={militar.id}
                        className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-[oklch(0.45_0.15_142)]/10 rounded-lg flex items-center justify-center overflow-hidden">
                            <img
                              src={`http://www.habbo.com.br/habbo-imaging/avatarimage?&user=${militar.nick}&action=std&direction=5&head_direction=2&img_format=png&gesture=std&headonly=1&size=l`}
                              alt={`Avatar de ${militar.nick}`}
                              className="max-w-full max-h-full object-contain"
                              onError={(e) => {
                                // Fallback para o ícone de escudo se a imagem não carregar
                                e.currentTarget.style.display = 'none';
                                e.currentTarget.nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                            <Shield className="w-5 h-5 text-[oklch(0.45_0.15_142)] hidden" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{militar.nick}</h3>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className="bg-[oklch(0.45_0.15_142)]/10 dark:bg-[oklch(0.45_0.15_142)]/20 text-[oklch(0.35_0.15_142)] dark:text-[oklch(0.55_0.15_142)] border-[oklch(0.45_0.15_142)]/20">
                                {militar.patente}
                              </Badge>
                              {militar.status && (
                                <Badge className={
                                  militar.status === 'Ativo'
                                    ? 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700'
                                    : militar.status === 'Inativo'
                                      ? 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700'
                                      : 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-700'
                                }>
                                  {militar.status}
                                </Badge>
                              )}
                              {militar.tem_acesso ? (
                                <Badge className={
                                  militar.acesso_ativo
                                    ? "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700"
                                    : "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700"
                                }>
                                  <KeyRound className="h-3 w-3 mr-1" />
                                  {militar.acesso_ativo ? "Acesso Ativo" : "Acesso Inativo"}
                                </Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600">
                                  <X className="h-3 w-3 mr-1" />
                                  Sem Acesso
                                </Badge>
                              )}
                              {militar.ultima_promocao && (
                                <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {formatDate(militar.ultima_promocao)}
                                </span>
                              )}
                            </div>
                            {militar.promovido_por && (
                              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                Promovido por: {militar.promovido_por}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {hasAnyAction(militar) && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                >
                                  <MoreVertical className="h-4 w-4" />
                                  <span className="sr-only">Ações</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                <DropdownMenuLabel>Ações</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                {canEditarMilitar(militar) && (
                                  <DropdownMenuItem
                                    className="cursor-pointer text-blue-600 dark:text-blue-400 focus:text-blue-600 dark:focus:text-blue-400"
                                    onClick={() => navigate(`/registros/militares/${militar.id}/editar`)}
                                  >
                                    <Pencil className="w-4 h-4 mr-2" />
                                    Editar Militar
                                  </DropdownMenuItem>
                                )}
                                {canEditarMilitar(militar) && (
                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    disabled={syncingWiredId === militar.id}
                                    onClick={() => handleSincronizarWired(militar)}
                                  >
                                    <RefreshCw className={`w-4 h-4 mr-2 ${syncingWiredId === militar.id ? "animate-spin" : ""}`} />
                                    Sincronizar WIRED
                                  </DropdownMenuItem>
                                )}
                                {canAlterarNick(militar) && (
                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() => handleAlterarNick(militar)}
                                  >
                                    <Edit className="w-4 h-4 mr-2" />
                                    Alterar Nick
                                  </DropdownMenuItem>
                                )}
                                {canGerenciarPerfis(militar) && (
                                  <DropdownMenuItem
                                    className="cursor-pointer text-purple-600 dark:text-purple-400 focus:text-purple-600 dark:focus:text-purple-400"
                                    onClick={() => handleAtribuirPerfis(militar)}
                                  >
                                    <UserCheck className="w-4 h-4 mr-2" />
                                    Gerenciar Perfis
                                  </DropdownMenuItem>
                                )}
                                {canResetSenha(militar) && (
                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() => handleResetPassword(militar)}
                                  >
                                    <LockOpen className="w-4 h-4 mr-2" />
                                    Reset Senha
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {/* Controles de Paginação */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-4 border-t border-border mt-4">
                    <div className="text-sm text-muted-foreground">
                      Página {page} de {totalPages} ({totalCount} registros)
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePrevPage}
                        disabled={page === 1 || loading}
                        className="flex items-center gap-1"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Anterior
                      </Button>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (page <= 3) {
                            pageNum = i + 1;
                          } else if (page >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = page - 2 + i;
                          }
                          return (
                            <Button
                              key={pageNum}
                              variant={page === pageNum ? "default" : "outline"}
                              size="sm"
                              onClick={() => {
                                setPage(pageNum);
                                fetchMilitares(searchTerm, pageNum);
                              }}
                              disabled={loading}
                              className={page === pageNum ? "bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)]" : ""}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleNextPage}
                        disabled={page === totalPages || loading}
                        className="flex items-center gap-1"
                      >
                        Próximo
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
        </div>

        {/* Painel Lateral */}
        <div className="space-y-6">
            {/* Estatísticas */}
            <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
              <CardHeader>
                <CardTitle className="text-[oklch(0.35_0.15_142)] dark:text-[oklch(0.55_0.15_142)] flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Estatísticas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Total de Militares</span>
                    <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700">
                      {totalCount}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Carregados</span>
                    <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700">
                      {militares.length}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Com Acesso Ativo</span>
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700">
                      {militares.filter(m => m.tem_acesso && m.acesso_ativo).length}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Sem Acesso</span>
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700">
                      {militares.filter(m => !m.tem_acesso).length}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Ações Rápidas */}
            <Card>
              <CardHeader>
                <CardTitle className="text-[oklch(0.35_0.15_142)] dark:text-foreground flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Ações Rápidas
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {hasPermission(PERMISSOES.CRIAR_ALTERAR_PAINEL_OFICIAL) && (
                    <Button
                      onClick={() => setIsPainelOficialModalOpen(true)}
                      className="w-full justify-start bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Criar Painel de Oficial
                    </Button>
                  )}
                  <Button
                    onClick={() => setIsPainelPracaModalOpen(true)}
                    variant="outline"
                    className="w-full justify-start"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Criar Painel Praça
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Avisos Importantes */}
            <Card className="border-l-4 border-l-amber-500">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="h-4 w-4" />
                  Avisos Importantes
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                  <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex items-start gap-2">
                    <LockOpen className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>Reset de senha gera uma senha aleatória exibida após o reset.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Edit className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>Edições ficam registradas no histórico do sistema.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Award className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>Busque por nick específico para resultados mais precisos.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <KeyRound className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>Status de acesso indica se o militar possui painel ativo no sistema.</span>
                  </div>
                </div>
              </CardContent>
            </Card>
        </div>
      </div>

      {/* Modal Alterar Nick */}
      {selectedMilitar && (
        <AlterarNickModal
          isOpen={isAlterarNickModalOpen}
          onClose={handleCloseModal}
          militarNick={selectedMilitar.nick}
          onSuccess={handleNickUpdateSuccess}
        />
      )}

      {/* Modal Painel de Oficial */}
      <PainelOficialModal
        isOpen={isPainelOficialModalOpen}
        onClose={() => setIsPainelOficialModalOpen(false)}
        onSuccess={() => {
          setPage(1);
          fetchMilitares(searchTerm, 1);
          setIsPainelOficialModalOpen(false);
        }}
      />

      {/* Modal Painel de Praça */}
      <PainelPracaModal
        isOpen={isPainelPracaModalOpen}
        onClose={() => setIsPainelPracaModalOpen(false)}
        onSuccess={() => {
          setPage(1);
          fetchMilitares(searchTerm, 1);
          setIsPainelPracaModalOpen(false);
        }}
      />

      {/* Modal Atribuir Perfis */}
      {selectedMilitar && (
        <AtribuirPerfisModal
          isOpen={isAtribuirPerfisModalOpen}
          onClose={handleCloseAtribuirPerfis}
          militar={selectedMilitar}
          onSuccess={handlePerfisUpdateSuccess}
        />
      )}

      {/* Dialog Nova Senha */}
      <Dialog open={!!resetPasswordResult} onOpenChange={() => setResetPasswordResult(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              Senha Resetada
            </DialogTitle>
            <DialogDescription>
              A senha de <strong>{resetPasswordResult?.nick}</strong> foi resetada com sucesso. Copie a nova senha abaixo.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2">
            <p className="text-sm text-muted-foreground mb-2">Nova senha:</p>
            <button
              type="button"
              onClick={handleCopySenha}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-border bg-muted hover:bg-muted/80 transition-colors cursor-pointer group"
            >
              <code className="text-lg font-mono font-semibold tracking-widest text-foreground">
                {resetPasswordResult?.senha}
              </code>
              <span className="flex items-center gap-1 text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                {copied ? (
                  <>
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-green-500">Copiado!</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    <span>Copiar</span>
                  </>
                )}
              </span>
            </button>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Clique na senha para copiá-la para a área de transferência
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
